import {StyleSheet} from "aphrodite";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

// TODO (russell): This should replace the shared styles media queries
import * as mq from "../media-queries.ts";

const Regular = 400;
const SemiBold = 600;

const common = {
    color: color.offBlack,
    display: "block",
} as const;

const SansSerifFamily = "Lato, 'Noto Sans', sans-serif";
const SerifFamily = "'Source Serif Pro', 'Noto Serif', serif";

// Map languages to their line height
// to avoid cutting off letters in "tall" languages
export const fontTypes = {
    as: "large",
    bn: "large",
    gu: "large",
    hi: "large",
    hy: "medium",
    ja: "medium",
    ka: "medium",
    ko: "medium",
    mr: "large",
    pa: "large",
    ta: "medium",
} as const;

// different fonts have different size ascenders and descenders
// 3 different size line heights are needed to prevent clipping of text
const serifHeadingLargeLineHeight = {
    default: {
        "mq.phoneOrSmaller": "26px",
        "mq.largePhoneOrSmaller": "31px",
        "mq.largePhoneOnly": "31px",
        "mq.smallTabletOrSmaller": "51px",
        "mq.smallTabletOnly": "31px",
        "mq.tabletOnly": "31px",
        "mq.smallDesktopOnly": "39px",
        "mq.desktopOnly": "44px",
        "mq.largeDesktopOrLarger": "54px",
    },
    medium: {
        "mq.phoneOrSmaller": "30px",
        "mq.largePhoneOrSmaller": "35px",
        "mq.largePhoneOnly": "35px",
        "mq.smallTabletOrSmaller": "61px",
        "mq.smallTabletOnly": "36px",
        "mq.tabletOnly": "36px",
        "mq.smallDesktopOnly": "39px",
        "mq.desktopOnly": "51px",
        "mq.largeDesktopOrLarger": "63px",
    },
    large: {
        "mq.phoneOrSmaller": "32px",
        "mq.largePhoneOrSmaller": "40px",
        "mq.largePhoneOnly": "40px",
        "mq.smallTabletOrSmaller": "68px",
        "mq.smallTabletOnly": "40px",
        "mq.tabletOnly": "40px",
        "mq.smallDesktopOnly": "39px",
        "mq.desktopOnly": "56px",
        "mq.largeDesktopOrLarger": "70px",
    },
} as const;

// helper function to get the correct line height
function getShlLineHeight(mediaQuery: string) {
    const sizeMap =
        serifHeadingLargeLineHeight[fontTypes[RequestInfo.kaLocale]] ||
        serifHeadingLargeLineHeight["default"];
    return sizeMap[mediaQuery];
}

export default StyleSheet.create({
    SansSerifAttribution: {
        ...common,
        fontFamily: SansSerifFamily,
        fontSize: 11,
        fontWeight: Regular,
        lineHeight: "17px",
        letterSpacing: "0.08em",
    },
    SansSerifBody: {
        ...common,
        fontFamily: SansSerifFamily,
        fontSize: 16,
        fontWeight: Regular,
        lineHeight: "26px",
        letterSpacing: -0.1,

        [mediaQueries.xs]: {
            fontSize: 14,
            lineHeight: "22px",
        },

        [mediaQueries.mdOrLarger]: {
            fontSize: 18,
            lineHeight: "30px",
        },
    },
    SansSerifBodySmall: {
        ...common,
        fontFamily: SansSerifFamily,
        fontSize: 16,
        fontWeight: Regular,
        lineHeight: "26px",
        letterSpacing: -0.1,

        [mediaQueries.xs]: {
            fontSize: 14,
            lineHeight: "22px",
        },
    },
    SansSerifTitleSmall: {
        ...common,
        fontFamily: SansSerifFamily,
        fontSize: 14,
        fontWeight: Regular,
        lineHeight: "18px",
        letterSpacing: "0.1em",
    },
    SerifHeadingDistrictsTitle: {
        ...common,
        fontFamily: SerifFamily,
        fontWeight: SemiBold,

        [mediaQueries.smOrSmaller]: {
            fontSize: 36,
            lineHeight: "39px",
        },
        [mediaQueries.md]: {
            fontSize: 50,
            lineHeight: "54px",
        },
        [mediaQueries.lgOrLarger]: {
            fontSize: 53,
            lineHeight: "58px",
        },
    },
    SerifHeadingLarge: {
        ...common,
        fontFamily: SerifFamily,
        fontSize: 30,
        fontWeight: SemiBold,
        lineHeight: "31px",

        [mq.phoneOrSmaller]: {
            fontSize: 25,
            lineHeight: getShlLineHeight("mq.phoneOrSmaller"),
        },
        [mq.largePhoneOrSmaller]: {
            fontSize: 30,
            lineHeight: getShlLineHeight("mq.largePhoneOrSmaller"),
        },
        [mq.largePhoneOnly]: {
            fontSize: 30,
            lineHeight: getShlLineHeight("mq.largePhoneOnly"),
        },
        [mq.smallTabletOrSmaller]: {
            fontSize: 51,
            lineHeight: getShlLineHeight("mq.smallTabletOrSmaller"),
        },
        [mq.smallTabletOnly]: {
            fontSize: 30,
            lineHeight: getShlLineHeight("mq.smallTabletOnly"),
        },
        [mq.tabletOnly]: {
            fontSize: 30,
            lineHeight: getShlLineHeight("mq.tabletOnly"),
        },
        [mq.smallDesktopOnly]: {
            fontSize: 38,
            lineHeight: getShlLineHeight("mq.smallDesktopOnly"),
        },
        [mq.desktopOnly]: {
            fontSize: 43,
            lineHeight: getShlLineHeight("mq.desktopOnly"),
        },
        [mq.largeDesktopOrLarger]: {
            fontSize: 53,
            lineHeight: getShlLineHeight("mq.largeDesktopOrLarger"),
        },
    },
    SerifHeadingHero: {
        ...common,
        fontFamily: SerifFamily,
        fontWeight: SemiBold,
        fontSize: 25,
        lineHeight: "26px",

        [mq.phoneOnly]: {
            fontSize: 30,
            lineHeight: "35px",
        },
        [mq.largePhoneOnly]: {
            fontSize: 30,
            lineHeight: "35px",
        },
        [mq.smallTabletOnly]: {
            fontSize: 43,
            lineHeight: "44px",
        },
        [mq.tabletOnly]: {
            fontSize: 44,
            lineHeight: "48px",
        },
        [mq.smallDesktopOnly]: {
            fontSize: 30,
            lineHeight: "31px",
        },
        [mq.desktopOnly]: {
            fontSize: 38,
            lineHeight: "39px",
        },
        [mq.largeDesktopOnly]: {
            fontSize: 43,
            lineHeight: "44px",
        },
        [mq.xlDesktopOnly]: {
            fontSize: 44,
            lineHeight: "48px",
        },
    },
    SerifHeadingMedium: {
        ...common,
        fontFamily: SerifFamily,
        fontSize: 30,
        fontWeight: SemiBold,
        lineHeight: "35px",
        letterSpacing: -0.1,

        [mq.tabletOrLarger]: {
            fontSize: 44,
            lineHeight: "48px",
        },

        [mq.phoneOrSmaller]: {
            fontSize: 30,
            lineHeight: "35px",
        },
    },
    SerifHeadingSmall: {
        ...common,
        fontFamily: SerifFamily,
        fontSize: 25,
        fontWeight: Regular,
        lineHeight: "30px",
        letterSpacing: -0.1,
    },
    SerifQuote: {
        ...common,
        fontFamily: SerifFamily,
        fontSize: 24,
        fontWeight: Regular,
        lineHeight: "32px",
        letterSpacing: -0.1,

        [mediaQueries.mdOrLarger]: {
            fontSize: 28,
            lineHeight: "38px",
        },

        [mediaQueries.lgOrLarger]: {
            fontSize: 32,
            lineHeight: "43px",
        },
    },
    SerifStat: {
        ...common,
        fontFamily: SerifFamily,
        fontSize: 22,
        fontWeight: Regular,
        lineHeight: "28px",

        [mediaQueries.mdOrLarger]: {
            fontSize: 20,
        },

        [mediaQueries.lgOrLarger]: {
            fontSize: 30,
            lineHeight: "34px",
        },
    },
    SerifBody: {
        ...common,
        fontFamily: SerifFamily,
        fontSize: 18,
        fontWeight: Regular,
        lineHeight: "26px",

        [mediaQueries.xs]: {
            fontSize: 16,
            lineHeight: "22px",
        },

        [mediaQueries.mdOrLarger]: {
            fontSize: 20,
            lineHeight: "30px",
        },
    },
});
